/* =========================================================================
   PEV — pev-inside-look.js : Beispielseiten-Reihe, Karussell & Großansicht
   -------------------------------------------------------------------------
   Feedback-Runde 08/2026 (Tia, Beispielseiten-Mail). Additiv, isoliert (NEUE
   Datei — KEIN Dawn-Core-JS-Eingriff). Geladen von
   sections/pev-product-inside-look.liquid.

   Definiert das Custom-Element <pev-inside-look>. Custom-Element statt
   DOMContentLoaded-Init, weil der Customizer Sections nachlädt: connectedCallback
   greift dort automatisch, ein einmaliger Init nicht.

   ZWEI DARSTELLUNGEN, ein Markup (Setting „Darstellung"):
   · reihe     — der Scroll-Container bleibt, wie er ist; dieses Skript ergänzt
                 nur Pfeile.
   · karussell — dieselben Kacheln werden absolut gestapelt und per 3D-Transform
                 gestaffelt (eine Seite vorn, Nachbarn kleiner und weggedreht).

   PROGRESSIVE ENHANCEMENT — ohne dieses Skript bleibt alles bedienbar:
   · Die Reihe ist ein CSS-Scroll-Container mit Snap ⇒ wischen und Tastatur
     funktionieren ohne JS. Die Karussell-Optik hängt an einer Klasse, die NUR
     dieses Skript setzt ⇒ ohne JS bleibt die Reihe stehen, nichts bricht.
   · Jede Kachel ist ein <a> auf die Bilddatei ⇒ ohne JS öffnet der Klick das
     große Bild direkt.

   WARUM natives <dialog> für die Großansicht: Esc, Fokusfalle,
   Hintergrund-Inertheit und Fokus-Rückgabe an den auslösenden Link kommen vom
   Browser. Damit keine zweite Esc-Behandlung neben Dawns Cart-Drawer (dessen
   document-Handler ist auf .active gescopt, PR #52) und kein eigener
   Focus-Trap, der mit trapFocus konkurriert.
   ========================================================================= */
if (!customElements.get('pev-inside-look')) {
  customElements.define(
    'pev-inside-look',
    class PevInsideLook extends HTMLElement {
      /* Staffelung je Abstand zur vorderen Seite. Bewusst moderat: die Kacheln
         sind A4-Textseiten, nicht Plattencover — ab etwa 35 Grad kippt die
         Schrift so stark, dass man nicht mehr erkennt, was auf der Seite steht.
         x ist prozentual zur Kachelbreite, z in px, ry in Grad. */
      static TIERS = [
        { x: 0, z: 0, ry: 0, s: 1, o: 1, zi: 30 },
        { x: 74, z: -160, ry: 26, s: 0.84, o: 1, zi: 20 },
        { x: 128, z: -320, ry: 32, s: 0.7, o: 0.62, zi: 10 },
      ];

      static HIDDEN_TIER = { x: 146, z: -420, ry: 34, s: 0.6, o: 0, zi: 0 };

      connectedCallback() {
        this.row = this.querySelector('[data-pev-inside-row]');
        if (!this.row) return;

        this.prevButton = this.querySelector('[data-pev-inside-prev]');
        this.nextButton = this.querySelector('[data-pev-inside-next]');
        this.dialog = this.querySelector('[data-pev-inside-dialog]');
        this.dialogImage = this.querySelector('[data-pev-inside-dialog-img]');
        this.dialogCount = this.querySelector('[data-pev-inside-count]');
        this.status = this.querySelector('[data-pev-inside-status]');
        this.zoomLinks = Array.from(this.querySelectorAll('[data-pev-zoom]'));
        this.items = Array.from(this.querySelectorAll('.pev-inside__item'));
        this.currentIndex = 0;
        this.activeIndex = 0;
        this.isCarousel = this.dataset.pevInsideMode === 'karussell';

        if (this.isCarousel) this.setupCarousel();
        else this.setupRow();

        this.setupDialog();
      }

      disconnectedCallback() {
        if (this.onScroll) this.row.removeEventListener('scroll', this.onScroll);
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        this.unlockScroll();
      }

      reduceMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      /* ---------------------------------------------------------------- Reihe */

      setupRow() {
        if (!this.prevButton || !this.nextButton) return;

        this.prevButton.hidden = false;
        this.nextButton.hidden = false;
        this.prevButton.addEventListener('click', () => this.scrollRow(-1));
        this.nextButton.addEventListener('click', () => this.scrollRow(1));

        // Scroll-Position nur einmal pro Frame auswerten: das scroll-Event feuert
        // beim Wischen sehr dicht, der Zustand ändert sich aber nur an den Enden.
        let ticking = false;
        this.onScroll = () => {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(() => {
            ticking = false;
            this.updateNavState();
          });
        };
        this.row.addEventListener('scroll', this.onScroll, { passive: true });

        this.onResize = () => this.updateNavState();
        window.addEventListener('resize', this.onResize);

        this.updateNavState();
      }

      // Schrittweite = eine Kachel samt Abstand. Bewusst nicht die volle
      // Feldbreite: bei drei sichtbaren Kacheln überspränge ein „Seite weiter"
      // drei Seiten auf einmal und man verlöre den Zusammenhang von Doppelseiten.
      stepWidth() {
        const item = this.items[0];
        if (!item) return this.row.clientWidth;
        const styles = window.getComputedStyle(this.row);
        const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return item.getBoundingClientRect().width + gap;
      }

      scrollRow(direction) {
        this.row.scrollBy({
          left: this.stepWidth() * direction,
          behavior: this.reduceMotion() ? 'auto' : 'smooth',
        });
      }

      updateNavState() {
        if (!this.prevButton || !this.nextButton) return;

        const maxScroll = this.row.scrollWidth - this.row.clientWidth;
        // 2px Toleranz: Sub-Pixel-Rundung lässt scrollLeft am Ende oft knapp
        // unter maxScroll stehen, der Pfeil bliebe sonst dauerhaft aktiv.
        const scrollable = maxScroll > 2;

        this.classList.toggle('pev-inside__viewport--static', !scrollable);
        this.prevButton.disabled = !scrollable || this.row.scrollLeft <= 2;
        this.nextButton.disabled = !scrollable || this.row.scrollLeft >= maxScroll - 2;
      }

      /* ------------------------------------------------------------ Karussell */

      setupCarousel() {
        this.classList.add('pev-inside__viewport--carousel');

        if (this.prevButton && this.nextButton) {
          this.prevButton.hidden = false;
          this.nextButton.hidden = false;
          this.prevButton.addEventListener('click', () => this.goTo(this.activeIndex - 1));
          this.nextButton.addEventListener('click', () => this.goTo(this.activeIndex + 1));
        }

        this.row.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.goTo(this.activeIndex + 1);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.goTo(this.activeIndex - 1);
          }
        });

        // Tastaturfokus auf einer hinteren Kachel dreht sie nach vorn, sonst
        // stünde der Fokusring auf etwas, das man nicht sieht.
        //
        // NUR bei Tastaturfokus (:focus-visible): ein Mausklick setzt ebenfalls
        // Fokus, und zwar VOR dem click-Event. Ohne die Prüfung hätte der
        // Fokus-Handler die Kachel schon nach vorn gedreht, der Klick hätte
        // danach index === activeIndex gesehen und statt zu drehen sofort die
        // Großansicht aufgezogen. Genau so verhielt es sich im Test.
        this.zoomLinks.forEach((link, index) => {
          link.addEventListener('focus', () => {
            if (index === this.activeIndex) return;
            let keyboard = true;
            try {
              keyboard = link.matches(':focus-visible');
            } catch (error) {
              // Sehr alte Browser ohne :focus-visible — dann eben immer drehen.
            }
            if (keyboard) this.goTo(index);
          });
        });

        this.setupDrag();
        this.layout();
      }

      // Wischen ohne Bibliothek: Pointer-Events decken Maus, Touch und Stift ab.
      // touch-action: pan-y im CSS lässt vertikales Scrollen der Seite in Ruhe.
      setupDrag() {
        let startX = 0;
        let active = false;
        this.didDrag = false;

        // Ohne das startet der Browser beim Ziehen mit der Maus sein eigenes
        // Bild-Drag samt Ghost-Image (jedes <img> ist per Default draggable) —
        // das Wischen kam dann nie an. Beim Testen hing daran der ganze Zug.
        this.row.addEventListener('dragstart', (event) => event.preventDefault());

        this.row.addEventListener('pointerdown', (event) => {
          if (event.button !== 0) return;
          active = true;
          this.didDrag = false;
          startX = event.clientX;
        });

        this.row.addEventListener('pointermove', (event) => {
          if (!active) return;
          if (Math.abs(event.clientX - startX) > 10) this.didDrag = true;
        });

        const finish = (event) => {
          if (!active) return;
          active = false;
          const dx = event.clientX - startX;
          // 40px Schwelle: darunter ist es ein Klick mit unruhiger Hand.
          if (dx <= -40) this.goTo(this.activeIndex + 1);
          else if (dx >= 40) this.goTo(this.activeIndex - 1);
        };

        this.row.addEventListener('pointerup', finish);
        this.row.addEventListener('pointercancel', () => {
          active = false;
        });
      }

      goTo(index) {
        const total = this.items.length;
        if (!total) return;
        this.activeIndex = ((index % total) + total) % total;
        this.layout();
      }

      // Zyklischer Abstand: die Kachel „hinter" der letzten ist wieder die erste.
      // Damit dreht das Karussell endlos statt an den Enden anzuschlagen.
      offsetOf(index) {
        const total = this.items.length;
        let offset = index - this.activeIndex;
        if (offset > total / 2) offset -= total;
        if (offset < -total / 2) offset += total;
        return offset;
      }

      layout() {
        const total = this.items.length;

        this.items.forEach((item, index) => {
          const offset = this.offsetOf(index);
          const distance = Math.abs(offset);
          const tier = PevInsideLook.TIERS[distance] || PevInsideLook.HIDDEN_TIER;
          const sign = offset < 0 ? -1 : 1;
          const visible = distance < PevInsideLook.TIERS.length;

          item.style.setProperty('--pev-x', `${tier.x * sign}%`);
          item.style.setProperty('--pev-z', `${tier.z}px`);
          // Nach rechts versetzte Karten drehen sich nach links weg und umgekehrt.
          item.style.setProperty('--pev-ry', `${-tier.ry * sign}deg`);
          item.style.setProperty('--pev-s', tier.s);
          item.style.setProperty('--pev-o', tier.o);
          item.style.zIndex = tier.zi;
          item.dataset.pevPos = offset === 0 ? 'front' : visible ? 'side' : 'back';

          // Was man nicht sieht, gehört nicht in den Tab-Fluss.
          const link = this.zoomLinks[index];
          if (link) {
            link.tabIndex = visible ? 0 : -1;
            if (visible) link.removeAttribute('aria-hidden');
            else link.setAttribute('aria-hidden', 'true');
          }
        });

        if (this.status) {
          this.status.textContent = `Seite ${this.activeIndex + 1} von ${total}`;
        }
      }

      /* ----------------------------------------------------------- Großansicht */

      setupDialog() {
        // Kein <dialog>-Support (sehr alte Browser) ⇒ Links bleiben, was sie
        // sind: direkte Links auf die Bilddatei.
        const hasDialog = this.dialog && typeof this.dialog.showModal === 'function';

        this.zoomLinks.forEach((link, index) => {
          link.addEventListener('click', (event) => {
            // Modifier-Klicks (neuer Tab) und Mittelklick nicht abfangen.
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

            // Im Karussell holt ein Klick auf eine hintere Kachel diese erst
            // nach vorn — groß aufziehen tut nur die vordere.
            if (this.isCarousel) {
              if (this.didDrag) {
                event.preventDefault();
                return;
              }
              if (index !== this.activeIndex) {
                event.preventDefault();
                this.goTo(index);
                return;
              }
            }

            if (!hasDialog) return;
            event.preventDefault();
            this.open(index);
          });
        });

        if (!hasDialog) return;

        const closeButton = this.querySelector('[data-pev-inside-close]');
        if (closeButton) closeButton.addEventListener('click', () => this.dialog.close());

        const dialogPrev = this.querySelector('[data-pev-inside-dialog-prev]');
        const dialogNext = this.querySelector('[data-pev-inside-dialog-next]');
        if (dialogPrev) dialogPrev.addEventListener('click', () => this.show(this.currentIndex - 1));
        if (dialogNext) dialogNext.addEventListener('click', () => this.show(this.currentIndex + 1));

        // Klick auf den Backdrop schließt. Der Klick landet auf dem <dialog>
        // selbst; alles Sichtbare liegt in .pev-inside__dialog-inner.
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close();
        });

        this.dialog.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.show(this.currentIndex + 1);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.show(this.currentIndex - 1);
          }
        });

        // Esc schließt nativ ⇒ nur aufräumen, was wir selbst gesetzt haben.
        this.dialog.addEventListener('close', () => this.unlockScroll());
      }

      open(index) {
        this.show(index);
        this.dialog.showModal();
        this.lockScroll();
      }

      show(index) {
        const total = this.zoomLinks.length;
        if (!total) return;

        // Rundlauf: hinter der letzten Seite kommt wieder die erste.
        this.currentIndex = ((index % total) + total) % total;
        const link = this.zoomLinks[this.currentIndex];

        this.dialogImage.src = link.dataset.pevZoomFull || link.getAttribute('href');
        this.dialogImage.alt = link.dataset.pevZoomAlt || '';

        if (this.dialogCount) {
          this.dialogCount.textContent = `Seite ${this.currentIndex + 1} von ${total}`;
        }

        // Den Hintergrund mitführen, damit nach dem Schließen die zuletzt
        // betrachtete Seite dort steht, wo der Fokus hinzeigt.
        if (this.isCarousel) {
          this.goTo(this.currentIndex);
        } else {
          const item = link.closest('.pev-inside__item');
          if (item && typeof item.scrollIntoView === 'function') {
            item.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          }
        }
      }

      // showModal() hält den Hintergrund inert, verhindert aber kein Scrollen
      // des Dokuments. Eigene Klasse statt Inline-Style, damit Dawn-Klassen
      // unangetastet bleiben.
      lockScroll() {
        document.documentElement.classList.add('pev-inside-lightbox-open');
      }

      unlockScroll() {
        document.documentElement.classList.remove('pev-inside-lightbox-open');
      }
    }
  );
}
